<template>
    <SelectWrapper as="div" v-model="selectedOption" v-slot="{ open }" :class="selectWrapperMergedClasses" nullable>
        <SelectLabel class="block text-sm text-default mb-2" v-if="label">{{ label }}</SelectLabel>
        <div class="relative">
            <SelectButton
                as="div"
                id="selectButton"
                class="font-semibold relative text-left text-default flex flex-row items-center gap-2 transition-all duration-200"
                :class="[textClasses, selectButtonClasses]"
                :disabled="disabled"
            >
                <!-- Optional icon -->
                <Icon v-if="icon && iconPosition === 'left'" :type="icon" :filled="iconFilled" class="h-5 w-5 shrink-0" :class="textClasses" />

                <SelectInput
                    ref="selectInputRef"
                    @blur="clearQuery"
                    @change="query = $event.target.value"
                    class="block truncate grow pr-2 border-none focus:ring-0 outline-none w-full h-full cursor-[inherit] placeholder:text-sub placeholder:font-regular"
                    :displayValue="displayValue"
                    :placeholder="props.placeholder"
                />
                <Icon
                    v-if="open && selectedValue"
                    type="close"
                    class="w-4 p-1 shrink-0 bg-neutral-100 rounded-full hover:bg-neutral-200 cursor-pointer transition-all duration-200"
                    @click.stop="clearSelection"
                />
                <!-- Error icon -->
                <Icon v-if="state === 'error'" type="alert" class="shrink-0 pointer-events-none text-error-500" :class="size === 'lg' ? 'w-4' : 'w-3'" />

                <!-- Arrow icon -->
                <Icon type="triangleDown" class="transition-transform duration-200 ease-in-out" :class="[selectButtonArrowSizeClasses, caretClasses, { 'rotate-180': open }]" />
            </SelectButton>
            <transition
                enter-active-class="transition ease-out duration-100"
                enter-from-class="transform opacity-0 scale-98"
                enter-to-class="transform opacity-100 scale-100"
                leave-active-class="transition ease-in duration-75"
                leave-from-class="transform opacity-100 scale-100"
                leave-to-class="transform opacity-0 scale-98"
            >
                <SelectOptions
                    :class="[textSizeClasses, selectOptionsClasses]"
                    class="font-semibold absolute z-10 max-h-60 overflow-auto rounded-lg border border-surface-border bg-system-light shadow-md focus:outline-none"
                >
                    <SelectOption as="template" v-for="option in filteredOptions" :key="option.value" :value="option" v-slot="{ active }">
                        <li
                            class="flex px-4 py-2 items-center hover:bg-neutral-100 gap-2 cursor-pointer"
                            :class="[selectOptionSizeClasses, { 'bg-neutral-100 ': active || option.value === selectedValue }]"
                        >
                            <!-- Not using 'selected' v-slot value because it's not working with empty values -->
                            <div class="flex flex-col truncate grow gap-0.5">
                                <span class="block truncate grow">{{ option.label }}</span>
                                <span v-if="option.subLabel" class="block truncate grow text-sub">{{ option.subLabel }}</span>
                            </div>

                            <!-- Using custom selected check -->
                            <Icon v-if="option.value === selectedValue" type="check" class="w-3 shrink-0" />
                        </li>
                    </SelectOption>
                </SelectOptions>
            </transition>
        </div>
    </SelectWrapper>
</template>

<script setup lang="ts">
    import { computed, ref } from 'vue'
    import {
        Combobox as SelectWrapper,
        ComboboxInput as SelectInput,
        ComboboxButton as SelectButton,
        ComboboxLabel as SelectLabel,
        ComboboxOptions as SelectOptions,
        ComboboxOption as SelectOption
    } from '@headlessui/vue'
    import { Icon, IconType } from '../icons'
    import { twMerge } from 'tailwind-merge'

    type Option = { label: string; value: string; subLabel?: string; displayValue?: string }

    export type SelectAutocompleteProps = {
        class?: string
        disabled?: boolean
        icon?: IconType | undefined
        iconFilled?: boolean
        iconPosition?: 'left' | 'right'
        label?: string
        options: Option[]
        placeholder?: string
        rounded?: 'md' | 'full'
        size?: 'sm' | 'md' | 'lg'
        state?: 'error'
        variant?: 'light' | 'outline'
    }
    const props = withDefaults(defineProps<SelectAutocompleteProps>(), {
        class: undefined,
        disabled: false,
        icon: undefined,
        iconFilled: true,
        iconPosition: 'left',
        label: undefined,
        options: undefined,
        placeholder: 'Select option',
        rounded: 'full',
        size: 'md',
        state: undefined,
        variant: 'light'
    })

    const selectedValue = defineModel<string>({ required: false })

    const query = ref<string>('')
    const clearQuery = () => {
        query.value = ''
    }

    const selectInputRef = ref<InstanceType<typeof SelectInput> | null>(null)

    const clearSelection = () => {
        selectInputRef.value?.$el.focus()
        selectedValue.value = undefined
        clearQuery()
    }

    const filteredOptions = computed(() =>
        query.value === ''
            ? props.options
            : props.options.filter((option) => {
                  const displayValueMatches = option.displayValue?.toLowerCase()?.includes(query.value.toLowerCase())
                  const labelMatches = option.label.toLowerCase().includes(query.value.toLowerCase())
                  const subLabelMatches = option.subLabel?.toLowerCase().includes(query.value.toLowerCase())
                  return displayValueMatches || labelMatches || subLabelMatches
              })
    )

    // Create a computed property for the v-model binding
    const selectedOption = computed({
        get: (): Option | undefined => props.options.find((option) => option.value === selectedValue.value),
        set: (selected: Option) => {
            if (selected) setTimeout(() => selectInputRef.value?.$el.blur(), 1)
            query.value = ''
            selectedValue.value = selected?.value
        }
    })

    const displayValue = computed(() => {
        return () => selectedOption.value?.displayValue ?? selectedOption.value?.label ?? ''
    })

    const textSizeClasses = computed(() => [props.size === 'lg' ? 'text-sm' : 'text-xs'])
    const textClasses = computed(() => [
        ...textSizeClasses.value,
        {
            'text-default': !props.state && !props.disabled,
            'text-inactive': props.disabled
        }
    ])
    const caretClasses = computed(() => [
        {
            'text-neutral-500': !props.state && !props.disabled,
            'text-inactive': props.disabled
        }
    ])

    const selectWrapperSizeClasses = computed(() => {
        const sizeOptions = {
            sm: 'max-w-60',
            md: 'max-w-72',
            lg: 'max-w-96'
        }
        return [sizeOptions[props.size]]
    })
    const selectButtonClasses = computed(() => {
        const sizeOptions = {
            sm: 'h-8 px-3',
            md: 'h-10 px-5',
            lg: 'h-12 px-6'
        }
        const roundedOptions = {
            md: 'rounded-md',
            full: 'rounded-full'
        }
        const variantOptions = {
            light: 'bg-system-light shadow-sm outline-system-light outline outline-1 has-[:focus]:outline-surface-border',
            outline: 'outline-surface-border outline outline-1 has-[:focus]:outline-surface-border-contained'
        }
        const cursorClasses = props.disabled ? 'cursor-default' : 'cursor-pointer'

        const defaultClasses = ['w-full', cursorClasses, sizeOptions[props.size], roundedOptions[props.rounded], variantOptions[props.variant]]

        const overrideClasses: string[] = []
        if (props.state === 'error') overrideClasses.push('outline-error-500 outline outline-1')

        const mergedClasses = twMerge(defaultClasses, overrideClasses)
        return mergedClasses
    })
    const selectOptionsClasses = computed(() => {
        const sizeOptions = {
            sm: 'top-10 right-0',
            md: 'top-12 right-0',
            lg: 'top-14 right-0'
        }
        const roundedOptions = {
            md: 'rounded-md',
            full: 'rounded-2xl'
        }
        return [sizeOptions[props.size], roundedOptions[props.rounded]]
    })
    const selectOptionSizeClasses = computed(() => {
        const sizeOptions = {
            sm: 'min-h-10 px-3 max-w-60',
            md: 'min-h-10 px-3 max-w-72',
            lg: 'min-h-11 px-3 max-w-96'
        }
        return [sizeOptions[props.size], 'min-w-60 truncate']
    })
    const selectButtonArrowSizeClasses = computed(() => {
        const sizeOptions = {
            sm: 'w-2',
            md: 'w-2',
            lg: 'w-3'
        }
        return [sizeOptions[props.size], 'shrink-0']
    })

    // Allow overriding base classes
    const selectWrapperBaseClasses = [selectWrapperSizeClasses.value]
    const selectWrapperMergedClasses = computed(() => twMerge(selectWrapperBaseClasses, props.class))
</script>
